import {
  ApplicationService,
  RegistrationPeriod,
  RegistrationType,
  SaferCarrier,
} from '@/@types/generated'
import Loading from '@/components/Atoms/Loading'
import CancelApplicationDialog from '@/components/Molecules/Application/CancelApplicationDialog'
import { useError } from '@/hooks/useError'
import { buildPath } from '@/routes'
import {
  createContext,
  Dispatch,
  SetStateAction,
  use,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Outlet, useNavigate, useParams } from 'react-router'

export interface ApplicationContextType {
  carrier?: SaferCarrier
  businessEntityId?: string
  // The MCCS carrier the application belongs to
  carrierId?: string
  registrationType: RegistrationType | undefined
  setRegistrationType: Dispatch<SetStateAction<RegistrationType | undefined>>
  maxStepReached: number
  setMaxStepReached: Dispatch<SetStateAction<number>>
  showCancel: () => void
  registrationPeriod?: RegistrationPeriod
  setRegistrationPeriod: Dispatch<RegistrationPeriod>
}

const ApplicationContext = createContext<ApplicationContextType>(undefined!)

export const useApplication = (): ApplicationContextType => {
  const context = use<ApplicationContextType>(ApplicationContext)
  if (context === undefined) {
    throw new Error('useApplication must be used within an ApplicationContext')
  }
  return context
}

const ApplicationProvider = () => {
  const { applicationId } = useParams()
  const { showError } = useError()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(false)
  const [accessDenied, setAccessDenied] = useState(false)
  const [maxStepReached, setMaxStepReached] = useState<number>(1)
  const [registrationType, setRegistrationType] = useState<RegistrationType>()
  const [carrier, setCarrier] = useState<SaferCarrier>()
  const [businessEntityId, setBusinessEntityId] = useState<string>()
  const [carrierId, setCarrierId] = useState<string>()
  const [showingCancel, setShowingCancel] = useState(false)
  const [registrationPeriod, setRegistrationPeriod] =
    useState<RegistrationPeriod>()

  const _getApplication = useCallback(async () => {
    try {
      setIsLoading(true)

      if (!applicationId) return

      // application already carries step/registrationType, so this covers what a separate
      // getApplicationStep call would — fetch it alongside the carrier lookup instead of
      // sequentially after it.
      const [application, carrierResponse, registrationPeriodResp] =
        await Promise.all([
          ApplicationService.getApplication(applicationId),
          ApplicationService.getCarrierFromApplicationId(applicationId),
          ApplicationService.getRegistrationPeriod(applicationId),
        ])

      setMaxStepReached(application.step ?? 1)
      setRegistrationType(application.registrationType)
      setRegistrationPeriod(registrationPeriodResp)
      setBusinessEntityId(application.businessEntityId ?? undefined)
      setCarrierId(application.carrierId ?? undefined)
      setCarrier(carrierResponse)
    } catch (e: any) {
      switch (e.status) {
        case 409:
          // Not this user's application — someone else already owns an application for this
          // carrier (ApplicationPermissionHandler denial, mapped to 409 server-side). Bail out
          // of the wizard entirely rather than rendering a step with no data, which otherwise
          // lets the user click through several broken-looking steps.
          setAccessDenied(true)
          showError(
            'An application has already been started by a different user for this USDOT number. Please enter a different USDOT or contact TxDMV for assistance.'
          )
          navigate(buildPath('/applications'))
          break
        case 404:
          showError('Application not found. It may have been deleted.')
          navigate(buildPath('/applications'))
          break
        default:
          console.error('There was an error getting application: ', e)
          showError(
            'There was an error getting application.  Please try again.'
          )
      }
      // TODO: Handle 404 when applicationId is provided. Application may have been deleted
    } finally {
      setIsLoading(false)
    }
  }, [applicationId, navigate, showError])

  const showCancel = () => {
    setShowingCancel(true)
  }

  useEffect(() => {
    _getApplication()
  }, [_getApplication])

  return (
    <ApplicationContext.Provider
      value={{
        carrier,
        businessEntityId,
        carrierId,
        registrationType,
        setRegistrationType,
        maxStepReached,
        setMaxStepReached,
        registrationPeriod,
        setRegistrationPeriod,
        showCancel,
      }}
    >
      {isLoading || accessDenied ? (
        <Loading variant="fullscreen" />
      ) : (
        <Outlet />
      )}

      <CancelApplicationDialog
        open={showingCancel}
        onOpenChange={setShowingCancel}
      />
    </ApplicationContext.Provider>
  )
}

export default ApplicationProvider
